/**
 * Score helpers.
 *
 * Opta lumps penalty-shootout goals into `scores.total`. Consumers almost
 * always want the *open-play* score with the shoot-out tally exposed
 * separately, so each helper returns both as `{ score, pen }`.
 */

/** @typedef {import('./types').Match} Match */
/** @typedef {{ score: number; pen: number }} SideScore */

/**
 * Internal: compute open-play score + penalty shootout score for a side.
 *
 * @param {Match} [match]
 * @param {'home' | 'away'} side
 * @returns {SideScore | undefined}
 */
const getScoreForSide = (match, side) => {
  const scores = match?.liveData?.matchDetails?.scores;
  if (!scores?.total) return undefined;

  const pen = scores.pen?.[side] ?? 0;
  const score = scores.total[side] - pen;

  return { pen, score };
};

/**
 * Home team's open-play score and (if applicable) shoot-out tally.
 *
 * @param {Match} [match]
 * @returns {SideScore | undefined}
 */
export const getHomeScore = (match) => getScoreForSide(match, 'home');

/**
 * Away team's open-play score and (if applicable) shoot-out tally.
 *
 * @param {Match} [match]
 * @returns {SideScore | undefined}
 */
export const getAwayScore = (match) => getScoreForSide(match, 'away');
